'use client'

import { useTranslations } from 'next-intl'
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AccountTab } from '@/components/widget/account-tab'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { PublicAccount } from '@/types/generated/common/account'

// The "+N" trigger (size-6) plus the row's gap-1, in CSS px.
const MORE_BUTTON_WIDTH = 28
// The active tab is set semibold, so it measures a little wider than its inactive mirror.
const ACTIVE_TAB_SLACK = 8

interface OverflowAccountTabsProps {
  accounts: PublicAccount[]
  activeAccountId: string | undefined
  onSelectAccount: (accountId: string) => void
  /** Classes for the tab row itself (gap, padding, alignment). */
  className?: string
  hideIcon?: boolean
  plainInactive?: boolean
  maxWidthClassName?: string
  getTitle?: (account: PublicAccount) => string | undefined
  renderAccent?: (account: PublicAccount) => ReactNode
}

/** A row of AccountTabs that never scrolls: tabs that don't fit the row's width move into a
 * "+N" dropdown at its end, same as the dashboard's page tabs. The active account is always
 * kept on the row — when it would overflow, it takes the last visible slot. Widths come from
 * an invisible mirror of every tab, so the split is exact even for tabs currently in the menu. */
export function OverflowAccountTabs({
  accounts,
  activeAccountId,
  onSelectAccount,
  className,
  hideIcon,
  plainInactive,
  maxWidthClassName,
  getTitle,
  renderAccent,
}: OverflowAccountTabsProps) {
  const t = useTranslations('accountSelector')
  const containerRef = useRef<HTMLDivElement>(null)
  const mirrorRef = useRef<HTMLDivElement>(null)
  const [maxVisible, setMaxVisible] = useState(accounts.length)

  const activeIndex = accounts.findIndex((a) => a.accountId === activeAccountId)

  const recalculate = useCallback(() => {
    const container = containerRef.current
    const mirror = mirrorRef.current
    if (!container || !mirror) return
    const containerRect = container.getBoundingClientRect()
    if (!containerRect.width) return

    // Rects are in screen px; the fixed constants are CSS px. They differ under the
    // dashboard's UI-scale zoom.
    const scale = container.offsetWidth ? containerRect.width / container.offsetWidth : 1
    const style = getComputedStyle(container)
    const available =
      containerRect.width - (parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)) * scale
    const rects = Array.from(mirror.children, (el) => el.getBoundingClientRect())
    if (rects.length !== accounts.length) return
    const widths = rects.map((r) => r.width)
    const gap = rects.length > 1 ? rects[1].left - rects[0].right : 0
    const rowWidth = (indices: number[]) =>
      indices.reduce((sum, i) => sum + widths[i], 0) + gap * Math.max(0, indices.length - 1)

    const slack = ACTIVE_TAB_SLACK * scale
    let next = accounts.length
    if (rowWidth(accounts.map((_, i) => i)) + slack > available) {
      const budget = available - MORE_BUTTON_WIDTH * scale - slack
      const visibleIndices = (count: number) => {
        const indices = Array.from({ length: count }, (_, i) => i)
        if (activeIndex >= count) indices[count - 1] = activeIndex
        return indices
      }
      next = accounts.length - 1
      while (next > 1 && rowWidth(visibleIndices(next)) > budget) next--
    }
    setMaxVisible((current) => (current === next ? current : next))
  }, [accounts, activeIndex])

  useLayoutEffect(() => {
    recalculate()
  }, [recalculate])

  useEffect(() => {
    const container = containerRef.current
    const mirror = mirrorRef.current
    if (!container || !mirror) return
    // The mirror resizes when a name changes or fonts load; the container when the widget does.
    const observer = new ResizeObserver(() => recalculate())
    observer.observe(container)
    observer.observe(mirror)
    return () => observer.disconnect()
  }, [recalculate])

  let displayAccounts = accounts
  let overflowAccounts: PublicAccount[] = []
  if (accounts.length > maxVisible) {
    if (activeIndex >= maxVisible) {
      displayAccounts = [...accounts.slice(0, maxVisible - 1), accounts[activeIndex]]
      overflowAccounts = accounts.filter((_, i) => i >= maxVisible - 1 && i !== activeIndex)
    } else {
      displayAccounts = accounts.slice(0, maxVisible)
      overflowAccounts = accounts.slice(maxVisible)
    }
  }

  const renderTab = (account: PublicAccount, isActive: boolean) => (
    <AccountTab
      key={account.accountId}
      account={account}
      isActive={isActive}
      title={getTitle?.(account)}
      accent={renderAccent?.(account)}
      onSelect={() => onSelectAccount(account.accountId)}
      hideIcon={hideIcon}
      plainInactive={plainInactive}
      maxWidthClassName={maxWidthClassName}
    />
  )

  const moreLabel = t('moreAccounts', { count: overflowAccounts.length })

  return (
    <div ref={containerRef} className={cn('relative flex min-w-0 flex-1 items-end', className)}>
      {displayAccounts.map((account) => renderTab(account, account.accountId === activeAccountId))}
      {overflowAccounts.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="subtle"
              size="icon"
              aria-label={moreLabel}
              title={moreLabel}
              className="text-foreground/60 mb-0.5 size-6 shrink-0 p-0 text-xs font-medium"
            >
              +{overflowAccounts.length}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            {overflowAccounts.map((account) => (
              <DropdownMenuItem
                key={account.accountId}
                title={getTitle?.(account)}
                onSelect={() => onSelectAccount(account.accountId)}
                className="text-xs"
              >
                {!hideIcon && (
                  <ExchangeIcon
                    exchange={tradeVenueToIconKey(account.tradeVenue)}
                    className="size-3.5 shrink-0 rounded-full"
                  />
                )}
                <span className="min-w-0 truncate">{account.name}</span>
                {renderAccent?.(account)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {/* Zero-size, clipped mirror of every tab, laid out exactly like the row, so each tab
          — including those in the menu — has a measured width. */}
      <div aria-hidden className="pointer-events-none invisible absolute h-0 w-0 overflow-hidden">
        <div ref={mirrorRef} className={cn('flex w-max items-end', className)}>
          {accounts.map((account) => renderTab(account, false))}
        </div>
      </div>
    </div>
  )
}
